'use client'

import { useMemo, useState } from 'react'

import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'

import { cn, isFieldInvalid } from '@/utils'

import { FieldWrapper } from './field-wrapper'
import { useFieldContext } from './form'

export type DatePickerProps = {
  className?: string
  format?: (date: Date) => string
  label?: React.ReactNode
  placeholder?: string
  required?: boolean
}

export const DatePicker: React.FC<DatePickerProps> = ({
  className,
  format,
  label,
  placeholder,
  required = false
}) => {
  const t = useTranslations('components.form.datePicker')
  const field = useFieldContext<null | string>()
  const isInvalid = isFieldInvalid(field)
  const defaultPlaceholder = placeholder ?? t('selectDate')
  const [open, setOpen] = useState(false)

  const selectedDate = useMemo(() => {
    if (!field.state.value) return undefined
    const d = new Date(field.state.value)
    return isNaN(d.getTime()) ? undefined : d
  }, [field.state.value])

  const display = selectedDate
    ? format
      ? format(selectedDate)
      : selectedDate.toLocaleDateString()
    : defaultPlaceholder

  return (
    <FieldWrapper
      className={className}
      field={field}
      label={label}
      required={required}
    >
      <Popover onOpenChange={(o) => setOpen(o)} open={open}>
        <PopoverTrigger asChild>
          <Button
            className="justify-between"
            data-invalid={isInvalid || undefined}
            id={field.name}
            mode="input"
            onBlur={field.handleBlur}
            variant="outline"
          >
            <span className={cn(!selectedDate && 'text-muted-foreground')}>
              {display}
            </span>
            <span className="inline-flex items-center gap-1 opacity-60">
              <CalendarIcon className="size-4" />
              <ChevronDown className="size-4" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            defaultMonth={selectedDate}
            mode="single"
            onSelect={(date) => {
              field.handleChange(date ? date.toISOString() : null)
              setOpen(false)
            }}
            selected={selectedDate}
          />
        </PopoverContent>
      </Popover>
    </FieldWrapper>
  )
}
